import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "../../context/TranslationContext";
import HomeApi from "../../apiProvider/homeApi";
import { IMAGE_URL } from "../../network/apiClient";
import { logout } from "../../redux/authSlice";
import { setCartCount } from "../../redux/cartSlice";
import { setCurrency } from "../../redux/currencySlice";
import apiCart from "../../apiProvider/addToCartApi";
import Select from "react-select";
import apiProvider from "../../apiProvider/api";

const Header = () => {
    const { translateSync } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticated = useSelector((state) => state.auth.token);
    console.log("isAuthenticated", isAuthenticated)

    // State
    const [scrolled, setScrolled] = useState(false);
    const [showMegaMenu, setShowMegaMenu] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [categoryList, setCategoryList] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [productList, setProductList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [currencyList, setCurrencyList] = useState([]);
    const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
    const dispatch = useDispatch();
    const cartCount = useSelector((state) => state.cart.totalQuantity);

    // Scroll listener
    useEffect(() => {
        const handleScroll = () => {
            const offset = window.scrollY;
            if (offset > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        fetchCategories();
        getCartCount();
        fetchCurrencies();
    }, [isAuthenticated]);

    const fetchCurrencies = async () => {
        try {
            const response = await apiProvider.currencyList();
            if (response.status) {
                const currencies = Array.isArray(response.response) ? response.response : (response.response?.data || []);
                setCurrencyList(currencies);

                // Check if the currently selected currency in Redux exists in the fresh list
                const isSelectedValid = selectedCurrency && currencies.some(c => c._id === selectedCurrency.value);

                if (!isSelectedValid) {
                    // Find default currency if current is invalid
                    const defaultCurrency = currencies.find(c => c.isDefault);
                    if (defaultCurrency) {
                        dispatch(setCurrency({
                            value: defaultCurrency._id,
                            label: `${defaultCurrency.symbol}${defaultCurrency.code}`
                        }));
                    } else if (currencies.length > 0) {
                        dispatch(setCurrency({
                            value: currencies[0]._id,
                            label: `${currencies[0].symbol}${currencies[0].code}`
                        }));
                    } else {
                        // Clear selection if no currencies available
                        dispatch(setCurrency(null));
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching currencies:", error);
        }
    };

    const fetchCategories = async () => {
        try {
            setLoadingCategories(true);
            const response = await HomeApi.categoryList()
            if (response.status) {
                const data = response.response;
                if (Array.isArray(data)) {
                    setCategoryList(data);
                } else if (data && Array.isArray(data.data)) {
                    setCategoryList(data.data);
                } else {
                    console.error("Categories API returned non-array:", data);
                    setCategoryList([]);
                }
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            setCategoryList([]);
        } finally {
            setLoadingCategories(false);
        }
    };

    const getCartCount = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const guestId = localStorage.getItem('guestUserId');

            let result;
            if (token && user._id) {
                result = await apiCart.getCartCount(user._id, { type: 'user', userType: 'user' });
            } else if (guestId) {
                result = await apiCart.getCartCount(guestId, { type: 'guest', userType: 'guest' });
            }

            console.error("Cart Count API Response:", result?.response?.data);
            dispatch(setCartCount(result?.response?.data?.count || 0))

        } catch (error) {
            console.error("Error fetching cart details:", error);
        }
    };



    const fetchProducts = async (query = "") => {
        try {
            setIsSearching(true);
            const response = await HomeApi.ProductList({
                productName: query || undefined,
                limit: 10
            });
            if (response.status) {
                setProductList(response.response?.data || []);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setIsSearching(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchProducts(searchQuery);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery]);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: '#ddd',
            boxShadow: null,
            '&:hover': {
                borderColor: '#ddd'
            },
            minHeight: '50px',
            height: '50px',
            borderRadius: '8px',
            width: '250px',
            minWidth: '250px'
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: '50px',
            padding: '0 12px'
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '50px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? 'var(--primary-color)'
                : state.isFocused
                    ? '#EEEAE5'
                    : null,
            color: state.isSelected ? 'white' : 'var(--primary-color)',
            '&:active': {
                backgroundColor: 'var(--primary-color)',
                color: 'white'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--primary-color)'
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
        menu: (provided) => ({ ...provided, zIndex: 9999 })
    };

    const currencyStyles = {
        ...customStyles,
        control: (provided) => ({
            ...customStyles.control(provided),
            width: '120px',
            minWidth: '120px'
        })
    };

    const handleCurrencyChange = async (selectedOption) => {
        dispatch(setCurrency(selectedOption));
    };

    const handleProductSelect = (selectedOption) => {
        if (!selectedOption) return;
        const productId = selectedOption.value;

        const product = productList.find(p => p._id === productId);
        if (product) {
            console.error("Product:", product);
            const categoryId = product.categoryIdDetails?.[0]?._id || product.categoryId || 'all';
            const subCategoryId = product.subCategoryDetails?.[0]?._id || product.subCategoryId || 'all';
            navigate(`/products/${categoryId}/${subCategoryId}`, {
                state: {
                    productId: product._id
                }
            });
        }
    };

    const navbarClasses = `navbar navbar-expand-lg fixed-top industrial-navbar ${scrolled ? 'shadow-sm' : ''}`;

    const closeMenus = () => {
        setShowMegaMenu(false);
        setMobileMenuOpen(false);
    };

    return (
        <nav className={navbarClasses}>
            <div className="container-fluid" style={{ padding: '0 40px' }}>
                {/* 1. Left: Company Logo (PROVIDED LOGO) */}
                <NavLink className="navbar-brand" to="/" >
                    <img
                        src="/img/alpha-logo.png"
                        alt="ALPHA Technical Rubber Products"
                        style={{ height: '65px', objectFit: 'contain', transform: 'scale(1.4)', transformOrigin: 'left center' }} // Fixed height, scaling properly
                    />
                </NavLink>

                {/* Mobile Toggler */}
                <button
                    className="navbar-toggler"
                    type="button"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className={`collapse navbar-collapse ${mobileMenuOpen ? 'show' : ''}`}>
                    {/* 2. Center: Navigation Links */}
                    <ul className="navbar-nav mx-auto mb-2 mb-lg-0 align-items-center">
                        <li className="nav-item">
                            <NavLink className="nav-link" to="/" end>{translateSync('Home')}</NavLink>
                        </li>

                        {/* Products Mega Menu */}
                        <li
                            className="nav-item dropdown position-relative"
                            onMouseEnter={() => setShowMegaMenu(true)}
                            onMouseLeave={() => setShowMegaMenu(false)}
                        >
                            <a
                                className={`nav-link dropdown-toggle ${location.pathname.startsWith('/products') ? 'active' : ''}`}
                                href="#"
                                id="navbarDropdown"
                                role="button"
                                aria-expanded={showMegaMenu}
                            >
                                {translateSync('Products')}
                            </a>

                            {/* Simple Column-wise Category Dropdown */}
                            <div
                                className={`dropdown-menu border-0 shadow-sm ${showMegaMenu ? 'show' : ''}`}
                                style={{
                                    left: '0',
                                    width: '220px',
                                    padding: '8px 0',
                                    borderRadius: '8px',
                                    marginTop: '0',
                                    borderTop: '3px solid var(--primary-color) !important',
                                    visibility: showMegaMenu ? 'visible' : 'hidden',
                                    opacity: showMegaMenu ? 1 : 0,
                                    transform: showMegaMenu ? 'translateY(0)' : 'translateY(10px)',
                                    transition: 'all 0.3s ease',
                                    display: 'block',
                                    position: 'absolute',
                                    top: '100%',
                                    zIndex: 1000
                                }}
                            >
                                <div className="d-flex flex-column">
                                    {categoryList.map((category) => (
                                        <NavLink
                                            key={category._id}
                                            to={`/products/${category._id}`}
                                            className="dropdown-item dropdown-category-item py-2 px-3 d-flex align-items-center"
                                            onClick={() => closeMenus()}
                                        >
                                            <i className="bi bi-chevron-right me-2 category-icon"></i>
                                            <span className="category-name text-truncate">{category.name}</span>
                                        </NavLink>
                                    ))}
                                    {categoryList.length === 0 && (
                                        <div className="px-3 py-2 text-center text-muted small">
                                            {loadingCategories ? (
                                                <>
                                                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                                    Loading...
                                                </>
                                            ) : (
                                                "No Category Found"
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </li>

                        <li className="nav-item">
                            <NavLink className="nav-link" to="/applications">{translateSync('Applications')}</NavLink>
                        </li>
                        <li className="nav-item">
                            <NavLink className="nav-link" to="/resources">{translateSync('Resources')}</NavLink>
                        </li>
                        <li className="nav-item">
                            <NavLink className="nav-link" to="/about">{translateSync('About Us')}</NavLink>
                        </li>
                        <li className="nav-item">
                            <NavLink className="nav-link" to="/contact">{translateSync('Contact Us')}</NavLink>
                        </li>
                    </ul >

                    {/* Mobile Search & Currency (Visible only on Mobile) */}
                    <div className="d-lg-none p-3 border-top mobile-search-currency">
                        <div className="d-flex flex-column gap-3">
                            <Select
                                options={productList.map(product => ({
                                    value: product._id,
                                    label: product.productName
                                }))}
                                onChange={handleProductSelect}
                                onInputChange={(newValue) => setSearchQuery(newValue)}
                                placeholder="Search all products..."
                                styles={{
                                    ...customStyles,
                                    control: (provided) => ({
                                        ...customStyles.control(provided),
                                        width: '100%',
                                        minWidth: '100%'
                                    })
                                }}
                                isSearchable={true}
                                isClearable={true}
                                isLoading={isSearching}
                                menuPortalTarget={document.body}
                                value={null}
                                inputValue={searchQuery}
                            />

                            <Select
                                options={currencyList.map(currency => ({
                                    value: currency._id,
                                    label: `${currency.symbol}${currency.code}`
                                }))}
                                onChange={handleCurrencyChange}
                                placeholder="Select Currency"
                                styles={{
                                    ...currencyStyles,
                                    control: (provided) => ({
                                        ...currencyStyles.control(provided),
                                        width: '100%',
                                        minWidth: '100%'
                                    })
                                }}
                                isSearchable={false}
                                menuPortalTarget={document.body}
                                value={selectedCurrency}
                                isDisabled={location.pathname === "/checkout"}
                            />
                        </div>
                    </div>

                    {/* 3. Right: Icons */}
                    < div className="d-flex align-items-center gap-4" >


                        <div className="search-select-wrapper d-none d-lg-block">
                            <div className="d-flex align-items-center gap-2">
                                <Select
                                    options={productList.map(product => ({
                                        value: product._id,
                                        label: product.productName
                                    }))}
                                    onChange={handleProductSelect}
                                    onInputChange={(newValue) => setSearchQuery(newValue)}
                                    placeholder="Search all products..."
                                    styles={customStyles}
                                    isSearchable={true}
                                    isClearable={true}
                                    isLoading={isSearching}
                                    menuPortalTarget={document.body}
                                    value={null}
                                    inputValue={searchQuery}
                                />

                                <Select
                                    options={currencyList.map(currency => ({
                                        value: currency._id,
                                        label: `${currency.symbol}${currency.code}`
                                    }))}
                                    onChange={handleCurrencyChange}
                                    placeholder="Select"
                                    styles={currencyStyles}
                                    isSearchable={false}
                                    menuPortalTarget={document.body}
                                    value={selectedCurrency}
                                    isDisabled={location.pathname === "/checkout"}
                                />
                            </div>
                        </div>

                        {/* Cart */}
                        < div className="cart-wrapper position-relative" style={{ cursor: 'pointer' }} onClick={() => navigate('/cart')}>
                            <i className="bi bi-cart3 fs-4" style={{ color: 'var(--primary-color)' }}></i>
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
                                {cartCount}
                            </span>
                        </div >

                        {/* Login */}
                        {
                            isAuthenticated ? (
                                // <NavLink className="btn btn-sm btn-industrial-outline text-dark border-dark" to="/pageaccount">{translateSync('Dashboard')}</NavLink>
                                <div className="account-wrapper position-relative">
                                    <div className="d-flex align-items-center gap-2 account-trigger" style={{ cursor: "pointer" }} onClick={() => navigate('/pageaccount')}>
                                        <i className="bi bi-person fs-4" style={{ color: "var(--primary-color)" }}></i>
                                        <span className="fw-medium">Account</span>
                                    </div>

                                    {/* <div className="account-dropdown p-2">
                                        <NavLink className="dropdown-item d-flex align-items-center gap-2"
                                            to="/pageaccount">
                                            <i className="bi bi-person"></i> My Account
                                        </NavLink>

                                        <NavLink className="dropdown-item d-flex align-items-center gap-2"
                                            to="/order-tracking">
                                            <i className="bi bi-truck"></i> Order Tracking
                                        </NavLink>

                                        <NavLink className="dropdown-item d-flex align-items-center gap-2"
                                            to="/wishlist">
                                            <i className="bi bi-heart"></i> My Wishlist
                                        </NavLink>

                                        <hr className="my-2" />

                                        <button
                                            className="dropdown-item text-danger d-flex align-items-center gap-2"
                                            onClick={() => {
                                                dispatch(logout());
                                                dispatch(setCartCount(0));
                                                localStorage.removeItem("userToken");
                                                localStorage.removeItem("user");
                                                navigate("/");
                                            }}
                                        >
                                            <i className="bi bi-box-arrow-right"></i> Logout
                                        </button>
                                    </div> */}
                                </div>

                            ) : (
                                <NavLink className="btn btn-sm btn-industrial-primarys" to="/login" style={{ padding: "5px 15px", backgroundColor: "var(--primary-color)", color: "white", border: "none" }}>{translateSync('Login')}</NavLink>
                            )
                        }
                    </div >
                </div >
            </div >
            <style>
                {`
.account-wrapper {
    position: relative;
}

.account-dropdown {
    position: absolute;
    top: 40px;
    right: 0;
    width: 200px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
    opacity: 0;
    visibility: hidden;
    transform: translateY(8px);
    transition: all 0.2s ease;
    z-index: 1000;
}

.account-wrapper:hover .account-dropdown {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
}

.account-dropdown .dropdown-item {
    font-size: 14px;
    padding: 8px 12px;
    border-radius: 6px;
    text-decoration: none;
    color: #333;
    transition: background 0.2s ease;
}

.account-dropdown .dropdown-item:hover {
    background-color: #f5f5f5;
}

.account-dropdown .dropdown-item i {
    font-size: 14px;
}

/* Category Mega Menu Styling */
.mega-menu {
    border-top: 4px solid var(--primary-color) !important;
    animation: fadeInMenu 0.3s ease-in-out;
}

@keyframes fadeInMenu {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.category-card {
    background-color: #ffffff;
    border: 1px solid transparent;
    transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.category-card:hover {
    background-color: #ffffff;
    border-color: rgba(30, 41, 59, 0.1);
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
}

.category-card .category-icon-wrapper {
    transition: all 0.3s ease;
}

.category-card:hover .category-icon-wrapper {
    background-color: var(--primary-color) !important;
    border-color: var(--primary-color) !important;
}

.category-card:hover .category-icon-wrapper i {
    color: #ffffff !important;
}

/* Professional Header Dropdown Category Hover Styles */
.dropdown-category-item {
    font-size: 0.9rem;
    font-weight: 500;
    color: #334155 !important;
    background-color: transparent !important;
    transition: all 0.2s ease-in-out;
    border-left: 3px solid transparent;
}

.dropdown-category-item:hover {
    background-color: #f8fafc !important; /* Professional light blue-grey */
    color: var(--primary-color) !important;
    border-left: 3px solid var(--primary-color);
    padding-left: 1.25rem !important; /* Slight inward shift */
}

.dropdown-category-item .category-icon {
    font-size: 10px;
    color: var(--primary-color);
    transition: transform 0.2s ease-in-out;
}

.dropdown-category-item:hover .category-icon {
    transform: translateX(2px);
}

.category-card .category-title {
    transition: color 0.3s ease;
}

.category-card:hover .category-title {
    color: var(--primary-color) !important;
}

.category-arrow {
    opacity: 0;
    transform: translateX(-10px);
    transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
    color: var(--primary-color) !important;
}

.category-card:hover .category-arrow {
    opacity: 1;
    transform: translateX(0);
}
.nav-link {
    white-space: nowrap !important;
    font-weight: 500 !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
}

.industrial-navbar .navbar-nav {
    gap: 2px !important;
}

@media (max-width: 1400px) {
    .industrial-navbar .container-fluid {
        padding: 0 20px !important;
    }
    .nav-link {
        padding-left: 8px !important;
        padding-right: 8px !important;
        font-size: 0.9rem !important;
    }
@media (max-width: 991px) {
    .industrial-navbar .container-fluid {
        padding: 0 15px !important;
    }
    .navbar-collapse {
        background: #fff;
        margin-top: 10px;
        border-radius: 8px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    }
    .nav-link {
        padding: 12px 20px !important;
        border-bottom: 1px solid #f8f9fa;
    }
    .mobile-search-currency {
        background: #f8f9fa;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
    }
}
`}
            </style>

        </nav >
    );
}

export default Header;